import React, { useEffect, useState, useRef } from 'react';
import * as echarts from 'echarts';
import ReportsService from '../../services/reportsService';
import BusinessUnitService from '../../services/businessUnitService';

const MONTHS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MONTHS_FULL_TH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

const BAR_COLORS = ['#2563EB', '#F97316', '#60A5FA', '#FDBA74', '#34d399', '#f87171', '#a78bfa', '#fb923c'];
const BAR_RANK_COLORS = ['#2563EB', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'];

// key จาก API ต่างจากเดิม: เดิมใช้ 'in-progress' (hyphen) ตอนนี้ BE ส่ง 'in_progress' (underscore)
const SUMMARY_CARDS = [
    { icon: 'ri-file-list-fill',       color: '#2563EB', label: 'New',         key: 'new'         },
    { icon: 'bx bxs-hourglass-top',    color: '#F97316', label: 'In Progress', key: 'in_progress' },
    { icon: 'ri-checkbox-circle-fill', color: '#22C55E', label: 'Completed',   key: 'completed'   },
    { icon: 'ri-spam-fill',            color: '#FACC15', label: 'Failed',      key: 'failed'      },
    { icon: 'ri-close-circle-fill',    color: '#DC2626', label: 'Cancel',      key: 'cancel'      },
];

// map key ของ messenger.loads -> ป้ายที่โชว์ (ระดับงาน)
const LOAD_LABELS = {
    high:   { title: 'High load',   sub: '> 300',     color: '#1d4ed8' },
    mid:    { title: 'Mid load',    sub: '200-299',   color: '#3b82f6' },
    normal: { title: 'Normal',      sub: '150-199',   color: '#93c5fd' },
    low:    { title: 'Low load',    sub: '< 150',     color: '#dbeafe' },
};

const now = new Date();

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/** ดึง array -> map ตาม key เพื่อ lookup ง่าย เช่น summaryMap['new'].count */
const keyBy = (arr, keyField) =>
    (arr || []).reduce((acc, item) => {
        acc[item[keyField]] = item;
        return acc;
    }, {});

/**
 * BE ส่ง request ที่ไม่ได้ผูก BU มาเป็น id_business_unit: -1, name: ""
 * (เช่น request เก่าที่ยังไม่ผูกบริษัท) — ใส่ label ให้อ่านรู้เรื่องแทนที่จะโชว์ว่าง
 */
const companyLabel = (name, idBusinessUnit) => {
    if (name) return name;
    if (idBusinessUnit === -1) return 'ไม่ระบุบริษัท';
    return `BU ${idBusinessUnit}`;
};

// ─────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────

const useChart = (ref, options) => {
    useEffect(() => {
        if (!ref.current) return;
        const chart = echarts.init(ref.current);
        chart.setOption(options);
        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            chart.dispose();
            window.removeEventListener('resize', handleResize);
        };
    }, [options]);
};

// ─────────────────────────────────────────────────────────
// Chart Components
// ─────────────────────────────────────────────────────────

const LineChart = ({ points, total }) => {
    const ref = useRef(null);
    const days = points.map((p) => p.day);
    const counts = points.map((p) => p.count);

    useChart(ref, {
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
        grid: { left: 40, right: 20, top: 20, bottom: 30 },
        xAxis: {
            type: 'category',
            data: days.map(String),
            axisLine: { lineStyle: { color: '#e5e7eb' } },
            axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        yAxis: {
            type: 'value', axisLine: { show: false },
            splitLine: { lineStyle: { color: '#f3f4f6' } },
            axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        series: [{
            type: 'line', data: counts, smooth: true, symbol: 'circle', symbolSize: 4,
            lineStyle: { color: '#3b82f6', width: 2 },
            itemStyle: { color: '#3b82f6' },
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(59,130,246,0.25)' },
                    { offset: 1, color: 'rgba(59,130,246,0.02)' },
                ]),
            },
        }],
    });

    return (
        <div className="rp-card">
            <div className="rp-card__header">
                <span className="rp-card__title">ปริมาณคำขอรายวัน</span>
                <div className="rp-card__total">
                    <span className="rp-total-num">{total.toLocaleString()}</span>
                    <span className="rp-total-label">รวม (ครั้ง)</span>
                </div>
            </div>
            {points.length === 0
                ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>ไม่มีข้อมูล</div>
                : <div ref={ref} style={{ height: 220 }} />
            }
        </div>
    );
};

const BarChart = ({ month, year, department }) => {
    const ref = useRef(null);
    const companies = department?.companies || [];
    const departments = department?.departments || [];

    const series = companies.map((c, i) => ({
        name: companyLabel(c.name, c.id_business_unit),
        type: 'bar',
        data: departments.map((d) => {
            const match = (d.by_company || []).find((bc) => bc.id_business_unit === c.id_business_unit);
            return match?.count || 0;
        }),
        barMaxWidth: 16,
        itemStyle: { color: BAR_COLORS[i % BAR_COLORS.length], borderRadius: [3, 3, 0, 0] },
    }));

    useChart(ref, {
        tooltip: { trigger: 'axis' },
        legend: {
            type: 'scroll',              // ── legend เลื่อนได้แถวเดียว ไม่ wrap ทับกราฟ ──
            data: companies.map((c) => companyLabel(c.name, c.id_business_unit)),
            top: 0,
            left: 0,
            right: 40,                   // เว้นที่ให้ปุ่มเลื่อน (arrow) ทางขวา
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 12,
            pageIconSize: 10,
            pageButtonItemGap: 4,
            pageTextStyle: { color: '#6b7280', fontSize: 11 },
            textStyle: { color: '#6b7280', fontSize: 11 },
        },
        grid: { left: 40, right: 20, top: 45, bottom: 40 },   // ── top ลดลงเหลือแค่พื้นที่ legend 1 แถว ──
        xAxis: {
            type: 'category', data: departments.map((d) => d.department),
            axisLabel: { color: '#6b7280', fontSize: 11, rotate: departments.length > 5 ? 20 : 0 },
            axisLine: { lineStyle: { color: '#e5e7eb' } },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: '#f3f4f6' } },
            axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        series,
    });

    return (
        <div className="rp-card">
            <div className="rp-card__header">
                <div>
                    <span className="rp-card__title">ปริมาณการให้บริการแยกตามแผนก</span>
                    <div className="rp-card__sub">{MONTHS_FULL_TH[month - 1]} {year + 543}</div>
                </div>
            </div>
            {departments.length === 0
                ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>ไม่มีข้อมูล</div>
                : <div ref={ref} style={{ height: 220 }} />
            }
        </div>
    );
};

const DonutChart = ({ messenger }) => {
    const ref = useRef(null);
    const loads = messenger?.loads || [];
    const total = messenger?.messenger_count || 0;

    const segments = loads.map((l) => {
        const label = LOAD_LABELS[l.key] || { title: l.key, sub: '', color: '#94a3b8' };
        return { ...l, ...label };
    });

    useChart(ref, {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const seg = params.data.raw;
                return `<b>${params.name}</b><br/>Messenger: ${seg.messenger_count} คน<br/>งานรวม: ${seg.jobs_total}`;
            },
        },
        series: [{
            type: 'pie', radius: ['55%', '80%'], center: ['40%', '50%'],
            data: segments.map((s) => ({
                name: s.title,
                value: s.messenger_count,
                raw: s,
                itemStyle: { color: s.color },
            })),
            label: { show: false },
            emphasis: { scale: true, scaleSize: 5 },
        }],
    });

    return (
        <div className="rp-card">
            <div className="rp-card__header">
                <span className="rp-card__title">ปริมาณทำงาน Messenger</span>
                <span className="rp-card__sub">{total} คน</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ position: 'relative', flex: '0 0 180px' }}>
                    <div ref={ref} style={{ height: 200 }} />
                    <div className="rp-donut-center">
                        <div className="rp-donut-num">{total}</div>
                        <div className="rp-donut-label">Messenger</div>
                    </div>
                </div>
                <div className="rp-legend">
                    {segments.map((s, i) => (
                        <div key={i} className="rp-legend-item">
                            <span className="rp-legend-dot" style={{ background: s.color }} />
                            <div className="rp-legend-text">
                                <span>{s.title}</span>
                                <span className="rp-legend-sub">{s.sub}</span>
                            </div>
                            <div className="rp-legend-right">
                                <span className="rp-legend-val">{s.messenger_count} คน</span>
                                <span className="rp-legend-pct">{s.percentage}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Breakdown (แทนที่ CompanyTop5 + ServiceTypeView เดิม)
// รองรับทั้ง by_business_unit และ by_service_type แบบเดียวกัน
// เพราะ BE ส่ง items เป็น {key, name, count, percentage} เหมือนกันทั้งคู่
// ─────────────────────────────────────────────────────────

/**
 * BE ส่ง 2 shape ต่างกันตาม type:
 *   type: 'by_company'      -> breakdown.by_company      = { total, companies: [{id_business_unit, name, count, percentage}] }
 *   type: 'by_service_type' -> breakdown.by_service_type  = { total, items:     [{key, name, count, percentage}] }
 * ฟังก์ชันนี้ normalize ทั้งสอง shape ให้เป็น { id, name, count, percentage } เดียวกันก่อนเรนเดอร์
 */
const normalizeBreakdownItems = (type, group) => {
    if (type === 'by_company') {
        return (group?.companies || []).map((c) => ({
            id: c.id_business_unit,
            name: companyLabel(c.name, c.id_business_unit),
            count: c.count,
            percentage: c.percentage,
        }));
    }
    // by_service_type (หรือ type อื่นที่ยังไม่รู้จัก) — ลองอ่านจาก items เป็นค่า default
    return (group?.items || []).map((it) => ({
        id: it.key,
        name: it.name,
        count: it.count,
        percentage: it.percentage,
    }));
};

const BreakdownView = ({ breakdown, selectedBu, month, year }) => {
    const type = breakdown?.type; // 'by_company' | 'by_service_type'
    const group = breakdown?.[type] || {};
    const items = normalizeBreakdownItems(type, group).sort((a, b) => b.count - a.count);
    const top5 = items.slice(0, 5);
    const others = items.slice(5);
    const othersCount = others.reduce((s, i) => s + i.count, 0);
    const max = top5[0]?.count || 1;
    const total = group.total ?? 0;

    const title = type === 'by_service_type'
        ? 'ปริมาณตามประเภทบริการ'
        : 'ปริมาณการใช้งานในบริษัท';

    const sub = type === 'by_service_type'
        ? `${selectedBu?.code || ''} — ${MONTHS_TH[month - 1]} ${String(year + 543).slice(-2)}`
        : 'Top 5';

    return (
        <div className="rp-card">
            <div className="rp-card__header">
                <div>
                    <span className="rp-card__title">{title}</span>
                    <div className="rp-card__sub">{sub}</div>
                </div>
                <div className="rp-card__total">
                    <span className="rp-total-num">{total.toLocaleString()}</span>
                    <span className="rp-total-label">ครั้ง</span>
                </div>
            </div>
            <div className="rp-top5-list">
                {top5.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>ไม่มีข้อมูล</div>
                ) : (
                    top5.map((item, i) => (
                        <div key={item.id} className="rp-top5-item">
                            {type !== 'by_service_type' && (
                                <span className={`rp-top5-rank rp-top5-rank--${i + 1}`}>{i + 1}</span>
                            )}
                            <span className="rp-top5-name" style={type === 'by_service_type' ? { width: 'auto', flex: 1 } : undefined}>
                                {item.name}
                            </span>
                            <div className="rp-top5-bar-wrap">
                                <div
                                    className="rp-top5-bar"
                                    style={{
                                        width: `${(item.count / max) * 100}%`,
                                        background: BAR_RANK_COLORS[i] || BAR_RANK_COLORS[4],
                                    }}
                                />
                            </div>
                            <span className="rp-top5-val" style={{ color: i < 3 ? BAR_RANK_COLORS[i] : '#64748B' }}>
                                {item.count.toLocaleString()}
                            </span>
                        </div>
                    ))
                )}
                {others.length > 0 && (
                    <div className="rp-top5-others">
                        <span>อื่นๆ ({others.length} รายการ)</span>
                        <span>{othersCount.toLocaleString()}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const SummaryCard = ({ icon, color, count, label }) => (
    <div className="rp-summary-card">
        <div className="rp-summary-icon" style={{ background: color + '18' }}>
            <i className={icon} style={{ color }} />
        </div>
        <div>
            <div className="rp-summary-count">{count.toLocaleString()}</div>
            <div className="rp-summary-label">{label}</div>
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminReportDashboard = () => {
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [selectedYear, setSelectedYear]   = useState(now.getFullYear());
    const [showMonthDD, setShowMonthDD]     = useState(false);
    const [selectedBu, setSelectedBu]       = useState(null);
    const [showBuDD, setShowBuDD]           = useState(false);

    const [businessUnits, setBusinessUnits] = useState([]);
    const [dashboard, setDashboard]         = useState(null); // raw result.* จาก /reports/dashboard

    const buDDRef    = useRef(null);
    const monthDDRef = useRef(null);

    useEffect(() => {
        loadBusinessUnits();
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [selectedMonth, selectedYear, selectedBu]);

    useEffect(() => {
        const handler = (e) => {
            if (buDDRef.current    && !buDDRef.current.contains(e.target))    setShowBuDD(false);
            if (monthDDRef.current && !monthDDRef.current.contains(e.target)) setShowMonthDD(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const loadBusinessUnits = async () => {
        try {
            const resp = await BusinessUnitService.SearchBusinessUnit({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            setBusinessUnits(resp.data.result);
        } catch (err) { console.error(err); }
    };

    const loadDashboard = async () => {
        try {
            const resp = await ReportsService.GetDashboard({
                year: selectedYear,
                month: selectedMonth,
                id_business_unit: selectedBu?.id,
            });
            if (!resp.data.is_success || !resp.data.result) return;
            setDashboard(resp.data.result);
        } catch (err) { console.error(err); }
    };

    const summaryMap = keyBy(dashboard?.summary?.statuses, 'key');

    return (
        <div className="mc-page">
            <div className="mc-header">
                <div className="mc-header__left">
                    <h2 className="mc-title">Report - Dashboard</h2>
                    <p className="mc-subtitle">ภาพรวมสถิติและรายงานการใช้บริการ</p>
                </div>
                <div className="rp-filters">
                    <div style={{ position: 'relative' }} ref={buDDRef}>
                        <button className="rp-filter-btn" onClick={() => setShowBuDD(!showBuDD)}>
                            {selectedBu ? selectedBu.code : 'ทุกบริษัท'} <i className="ri-arrow-down-s-line" />
                        </button>
                        {showBuDD && (
                            <div className="rp-month-dd" style={{ minWidth: 200 }}>
                                <button
                                    className={`rp-month-dd__item ${!selectedBu ? 'rp-month-dd__item--active' : ''}`}
                                    style={{ gridColumn: '1 / -1', textAlign: 'left' }}
                                    onClick={() => { setSelectedBu(null); setShowBuDD(false); }}
                                >
                                    ทุกบริษัท
                                </button>
                                {businessUnits.map((bu) => (
                                    <button
                                        key={bu.id}
                                        className={`rp-month-dd__item ${selectedBu?.id === bu.id ? 'rp-month-dd__item--active' : ''}`}
                                        style={{ gridColumn: '1 / -1', textAlign: 'left' }}
                                        onClick={() => { setSelectedBu(bu); setShowBuDD(false); }}
                                    >
                                        {bu.name_th}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ position: 'relative' }} ref={monthDDRef}>
                        <button className="rp-filter-btn" onClick={() => setShowMonthDD(!showMonthDD)}>
                            {MONTHS_FULL_TH[selectedMonth - 1]} {selectedYear + 543} <i className="ri-arrow-down-s-line" />
                        </button>
                        {showMonthDD && (
                            <div className="rp-month-dd">
                                {/* ── แถวเลื่อนปี วางไว้บนตารางเดือน ── */}
                                <div
                                    style={{
                                        gridColumn: '1 / -1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 8,
                                        paddingBottom: 8,
                                        borderBottom: '1px solid #e5e7eb',
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="rp-year-nav-btn"
                                        onClick={() => setSelectedYear((y) => y - 1)}
                                    >
                                        <i className="ri-arrow-left-s-line" />
                                    </button>
                                    <span style={{ fontWeight: 600, fontSize: 14 }}>{selectedYear + 543}</span>
                                    <button
                                        type="button"
                                        className="rp-year-nav-btn"
                                        onClick={() => setSelectedYear((y) => y + 1)}
                                        disabled={selectedYear >= now.getFullYear()}
                                    >
                                        <i className="ri-arrow-right-s-line" />
                                    </button>
                                </div>

                                {MONTHS_TH.map((m, i) => (
                                    <button
                                        key={i}
                                        className={`rp-month-dd__item ${selectedMonth === i + 1 ? 'rp-month-dd__item--active' : ''}`}
                                        onClick={() => { setSelectedMonth(i + 1); setShowMonthDD(false); }}
                                    >
                                        {m}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="rp-summary-row">
                {SUMMARY_CARDS.map((c, i) => (
                    <SummaryCard
                        key={i}
                        icon={c.icon}
                        color={c.color}
                        count={summaryMap[c.key]?.count ?? 0}
                        label={c.label}
                    />
                ))}
            </div>

            <div className="rp-grid rp-grid--60-40">
                <LineChart
                    points={dashboard?.daily?.points || []}
                    total={dashboard?.daily?.total ?? 0}
                />
                <BreakdownView
                    breakdown={dashboard?.breakdown}
                    selectedBu={selectedBu}
                    month={selectedMonth}
                    year={selectedYear}
                />
            </div>

            <div className="rp-grid rp-grid--60-40">
                <BarChart
                    month={selectedMonth}
                    year={selectedYear}
                    department={dashboard?.department}
                />
                <DonutChart messenger={dashboard?.messenger} />
            </div>
        </div>
    );
};

export default AdminReportDashboard;